import { STORE_PLATFORMS, type StorePlatform } from './contracts/enums';

/**
 * Where else a game can be bought.
 *
 * **These are other people's shops, and that is the whole of what makes this
 * interesting.** A store page carrying a link to Steam is a link off our own
 * origin, put there by staff, rendered to the public. So the host is checked
 * rather than trusted: `normaliseStoreLink` parses the URL and refuses anything
 * whose host is not the platform's own, which is what stops *Also available on
 * Steam* pointing at somewhere that is not Steam.
 *
 * Kept in `@goodbuild/rules` rather than in the API because both halves need it
 * — the API refuses a bad link on the way in, and the app can say so before the
 * request is made — and because a second opinion about what counts as a Steam
 * URL is how a form and a server come to disagree about the same string.
 */

/** What the platform is called, as it appears to a reader. */
export const STORE_PLATFORM_LABELS: Readonly<Record<StorePlatform, string>> = Object.freeze({
  steam: 'Steam',
  epic: 'Epic Games Store',
  itch: 'itch.io',
});

export function storePlatformLabel(platform: StorePlatform): string {
  return STORE_PLATFORM_LABELS[platform];
}

/**
 * The order the store page lists them in.
 *
 * Declared rather than left to whatever order rows came back in, so the page
 * does not reorder itself when somebody adds a link. It is `STORE_PLATFORMS`'
 * own order, named here so the display reason is separate from the storage one.
 */
export const STORE_PLATFORM_ORDER: readonly StorePlatform[] = STORE_PLATFORMS;

/**
 * The hosts each platform actually serves its store pages from.
 *
 * `itch` is a suffix rather than an exact host: every itch.io game lives on its
 * developer's own subdomain (`somebody.itch.io/their-game`), so the check is
 * that the host *is* `itch.io` or ends with `.itch.io`. The leading dot is what
 * makes that safe — without it, `evil-itch.io` would pass.
 */
const EXACT_HOSTS: Readonly<Record<StorePlatform, readonly string[]>> = Object.freeze({
  steam: ['store.steampowered.com'],
  epic: ['store.epicgames.com'],
  itch: ['itch.io'],
});

const SUFFIX_HOSTS: Readonly<Record<StorePlatform, readonly string[]>> = Object.freeze({
  steam: [],
  epic: [],
  itch: ['.itch.io'],
});

export type StoreLinkResult = { ok: true; url: string } | { ok: false; message: string };

/**
 * A pasted link, checked and reduced to the address that will be stored.
 *
 * **Everything after the path is dropped.** A link copied out of a browser
 * carries whatever the last campaign put on it — `?utm_source=`, a referral tag,
 * a session fragment — and none of it belongs on a public page pointing at
 * somebody else's shop. Query and hash go; the path stays, because the path is
 * which game it is.
 *
 * `http` is upgraded rather than refused. Somebody pasting the address of a
 * store page has not made a security decision, and all three of these serve
 * HTTPS; storing the plain form would put a downgrade on a public page.
 *
 * Steam is canonicalised to `/app/<id>/`, which is the one platform where the
 * link has a second job: the app id in it is what the description and art
 * imports ask Valve about, so a stored Steam link that no id can be read out of
 * would be a link that looks fine and quietly breaks both importers.
 */
export function normaliseStoreLink(platform: StorePlatform, input: string): StoreLinkResult {
  const trimmed = input.trim();
  const label = storePlatformLabel(platform);

  if (trimmed === '')
    return { ok: false, message: `Paste the ${label} address, or leave it empty.` };

  let url: URL;
  try {
    // Tolerates a link copied without its scheme, which is what the address bar
    // hands over on some browsers.
    url = new URL(/^[a-z]+:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`);
  } catch {
    return { ok: false, message: `That is not a link. Paste the ${label} address of the game.` };
  }

  if (url.protocol !== 'https:' && url.protocol !== 'http:') {
    return { ok: false, message: `That is not a web address. Paste the ${label} page.` };
  }

  const host = url.hostname.toLowerCase().replace(/^www\./, '');
  const allowed =
    EXACT_HOSTS[platform].includes(host) ||
    SUFFIX_HOSTS[platform].some((suffix) => host.endsWith(suffix));

  if (!allowed) {
    return {
      ok: false,
      message: `That is not a ${label} address. It has to be a link to the game's page on ${label}.`,
    };
  }

  if (platform === 'steam') {
    const segments = url.pathname.split('/').filter(Boolean);
    const marker = segments.indexOf('app');
    const candidate = marker === -1 ? undefined : segments[marker + 1];

    if (candidate === undefined || !/^\d{1,8}$/.test(candidate)) {
      return {
        ok: false,
        message:
          'That Steam link does not name a game. Paste the address of its store page, such as ' +
          'https://store.steampowered.com/app/730/.',
      };
    }

    return { ok: true, url: `https://store.steampowered.com/app/${Number(candidate)}/` };
  }

  const path = url.pathname.replace(/\/+$/, '');
  if (path === '') {
    return {
      ok: false,
      message: `That is the ${label} front page. Paste the address of the game's own page.`,
    };
  }

  return { ok: true, url: `https://${host}${path}` };
}
